import React, { createContext, useState, useContext, useCallback, useRef } from 'react';

// Create notification context
export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);
  const timeoutRef = useRef(null);
  
  // Clear any existing timeout when unmounting
  const clearNotificationTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  
  // Show a notification
  const showNotification = useCallback((message, type = 'success', duration = 5000) => {
    // Clear any existing timeout
    clearNotificationTimeout();
    
    // Set the new notification with a unique ID
    setNotification({ message, type, id: Date.now() });
    
    // Auto-dismiss after duration if specified
    if (duration) {
      timeoutRef.current = setTimeout(() => {
        dismissNotification();
      }, duration);
    }
  }, [clearNotificationTimeout]);
  
  // Dismiss the notification
  const dismissNotification = useCallback(() => {
    clearNotificationTimeout();
    setNotification(null);
  }, [clearNotificationTimeout]);
  
  // Clean up timeouts when provider unmounts
  React.useEffect(() => {
    return () => {
      clearNotificationTimeout();
    };
  }, [clearNotificationTimeout]);
  
  return (
    <NotificationContext.Provider value={{ notification, showNotification, dismissNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotification = () => useContext(NotificationContext);

export default NotificationProvider; 